import graphql__ae2dae0befba6bb5a460 from "./__generated__/AnalysisOpenItemsViewQuery.graphql.ts";import * as React from "react";
import {startTransition, useState} from "react";
import {useOutletContext} from "react-router";
import {usePreloadedQuery, useQueryLoader} from "react-relay/hooks";
import {graphql} from "react-relay";
import {FormattedDate, useIntl} from "react-intl";
import {formatAmount} from "../helper/revenueFormat";
import KpiCard from "./presentational/KpiCard";
import Panel from "./presentational/Panel";
import StatusBadge from "./presentational/StatusBadge";
import AnalysisOpenItemsViewQueryNode from "./__generated__/AnalysisOpenItemsViewQuery.graphql";
import type {AnalysisOpenItemsViewQuery as AnalysisOpenItemsViewQueryType} from "./__generated__/AnalysisOpenItemsViewQuery.graphql";

const analysisOpenItemsViewQuery = graphql__ae2dae0befba6bb5a460;

type AnalysisOutletContext = {
    openItemsQuery: any
};

/**
 * The last day of the year before the current one - the cut-off date an open items report is
 * asked for most of the time.
 */
export function defaultCutOffYear(): number {
    return new Date().getUTCFullYear() - 1;
}

export function cutOffDateForYear(year: number): string {
    return `${year}-12-31`;
}

/** Invoices left over from before this year are of no interest to a report about it. */
const SELECTABLE_YEARS_BACK = 8;

const AnalysisOpenItemsView: React.FC = () => {
    const intl = useIntl();
    const {openItemsQuery} = useOutletContext<AnalysisOutletContext>();
    const [year, setYear] = useState(defaultCutOffYear());
    const [limitToYear, setLimitToYear] = useState(false);
    const [queryRef, loadOpenItemsQuery] = useQueryLoader<AnalysisOpenItemsViewQueryType>(AnalysisOpenItemsViewQueryNode, openItemsQuery);

    const reload = (next: { year?: number, limitToYear?: boolean }) => {
        const newYear = next.year ?? year;
        const newLimitToYear = next.limitToYear ?? limitToYear;
        startTransition(() => {
            setYear(newYear);
            setLimitToYear(newLimitToYear);
            loadOpenItemsQuery(
                {
                    cutOffDate: cutOffDateForYear(newYear),
                    invoiceYear: newLimitToYear ? newYear : null,
                },
                {fetchPolicy: 'store-and-network'},
            );
        });
    };

    const data = usePreloadedQuery<AnalysisOpenItemsViewQueryType>(analysisOpenItemsViewQuery, queryRef ?? openItemsQuery);
    const report = data.openItems;
    const invoices = report.invoices;

    const cutOffDate = new Date(report.cutOffDate);
    const writtenOffCount = invoices.filter(invoice => invoice.state === 'VOID' || invoice.state === 'REVERSED').length;
    const collectableGrossAmount = report.totalGrossAmount - report.writtenOffGrossAmount;

    const currentYear = new Date().getUTCFullYear();
    const selectableYears = Array.from({length: SELECTABLE_YEARS_BACK}, (_, index) => currentYear - index);

    const getInvoiceStateLabel = (state: string): string => {
        switch (state) {
            case 'OPEN':
                return intl.formatMessage({id: 'list.invoice.state.open'});
            case 'PAID':
                return intl.formatMessage({id: 'list.invoice.state.paid'});
            case 'REFUNDED':
                return intl.formatMessage({id: 'list.invoice.state.refunded'});
            case 'REVERSED':
                return intl.formatMessage({id: 'list.invoice.state.reversed'});
            case 'UNCOLLECTIBLE':
                return intl.formatMessage({id: 'list.invoice.state.uncollectible'});
            case 'VOID':
                return intl.formatMessage({id: 'list.invoice.state.void'});
            default:
                return state;
        }
    };

    /** Full days an invoice was past its due date when the cut-off date was reached. */
    const daysOverdueAt = (dueDate: string): number => {
        const due = new Date(dueDate);
        due.setUTCHours(0, 0, 0, 0);
        const days = Math.floor((cutOffDate.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
        return days > 0 ? days : 0;
    };

    return (
        <div className="space-y-6 px-4 sm:px-6 lg:px-0">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-medium text-gray-900">
                    {intl.formatMessage({id: 'view.analysisOpenItems.headline'}, {
                        date: intl.formatDate(cutOffDate, {day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC'})
                    })}
                </h2>
                <div className="flex flex-wrap items-center gap-3">
                    <select
                        value={year}
                        onChange={event => reload({year: Number(event.target.value)})}
                        className="border border-gray-300 rounded pl-3 pr-8 py-1.5 text-sm"
                    >
                        {selectableYears.map(selectableYear => (
                            <option key={selectableYear} value={selectableYear}>
                                {intl.formatMessage({id: 'view.analysisOpenItems.cutOffDateOption'}, {year: selectableYear})}
                            </option>
                        ))}
                    </select>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={limitToYear}
                            onChange={event => reload({limitToYear: event.target.checked})}
                            className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        {intl.formatMessage({id: 'view.analysisOpenItems.limitToYear'}, {year})}
                    </label>
                </div>
            </div>

            <p className="text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisOpenItems.intro'})}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                    label={intl.formatMessage({id: 'view.analysisOpenItems.kpi.count'})}
                    value={String(invoices.length)}
                />
                <KpiCard
                    label={intl.formatMessage({id: 'view.analysisOpenItems.kpi.gross'})}
                    value={formatAmount(report.totalGrossAmount)}
                    sub={intl.formatMessage({id: 'view.analysisOpenItems.kpi.gross.sub'}, {amount: formatAmount(report.totalNetAmount)})}
                />
                <KpiCard
                    label={intl.formatMessage({id: 'view.analysisOpenItems.kpi.collectable'})}
                    value={formatAmount(collectableGrossAmount)}
                    sub={intl.formatMessage({id: 'view.analysisOpenItems.kpi.collectable.sub'}, {count: invoices.length - writtenOffCount})}
                />
                <KpiCard
                    label={intl.formatMessage({id: 'view.analysisOpenItems.kpi.writtenOff'})}
                    value={formatAmount(report.writtenOffGrossAmount)}
                    sub={intl.formatMessage({id: 'view.analysisOpenItems.kpi.writtenOff.sub'}, {count: writtenOffCount})}
                />
            </div>

            <Panel headline={intl.formatMessage({id: 'view.analysisOpenItems.tableHeadline'})}>
                {invoices.length === 0 ? (
                    <p className="text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisOpenItems.empty'})}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                    <th className="text-left pb-2 pr-3">{intl.formatMessage({id: 'view.analysisOpenItems.col.invoiceNumber'})}</th>
                                    <th className="text-left pb-2 px-3">{intl.formatMessage({id: 'view.analysisOpenItems.col.invoiceDate'})}</th>
                                    <th className="text-left pb-2 px-3">{intl.formatMessage({id: 'view.analysisOpenItems.col.dueDate'})}</th>
                                    <th className="text-left pb-2 px-3">{intl.formatMessage({id: 'view.analysisOpenItems.col.customer'})}</th>
                                    <th className="text-right pb-2 px-3">{intl.formatMessage({id: 'view.analysisOpenItems.col.net'})}</th>
                                    <th className="text-right pb-2 px-3">{intl.formatMessage({id: 'view.analysisOpenItems.col.gross'})}</th>
                                    <th className="text-left pb-2 px-3">{intl.formatMessage({id: 'view.analysisOpenItems.col.stateToday'})}</th>
                                    <th className="text-left pb-2 pl-3">{intl.formatMessage({id: 'view.analysisOpenItems.col.settledAt'})}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map(invoice => {
                                    const daysOverdue = daysOverdueAt(invoice.dueDate);
                                    return (
                                        <tr key={invoice.id} className="border-t border-gray-100">
                                            <td className="py-1.5 pr-3 text-gray-900 whitespace-nowrap">
                                                {invoice.invoiceNumber}
                                                {invoice.type === 'REVERSE' && (
                                                    <span className="ml-2 text-xs text-gray-500">{intl.formatMessage({id: 'view.analysisOpenItems.reverseInvoice'})}</span>
                                                )}
                                            </td>
                                            <td className="py-1.5 px-3 whitespace-nowrap">
                                                <FormattedDate value={invoice.invoiceDate} day="2-digit" month="2-digit" year="numeric"/>
                                            </td>
                                            <td className={"py-1.5 px-3 whitespace-nowrap" + (daysOverdue >= 30 ? ' text-red-600' : daysOverdue >= 14 ? ' text-orange-500' : '')}>
                                                <FormattedDate value={invoice.dueDate} day="2-digit" month="2-digit" year="numeric"/>
                                                {daysOverdue > 0 && <span className="ml-1">(+{daysOverdue})</span>}
                                            </td>
                                            <td className="py-1.5 px-3">{invoice.address.businessName}</td>
                                            <td className="py-1.5 px-3 text-right whitespace-nowrap">{formatAmount(invoice.totalNetAmount)}</td>
                                            <td className="py-1.5 px-3 text-right whitespace-nowrap">{formatAmount(invoice.totalGrossAmount)}</td>
                                            <td className="py-1.5 px-3">
                                                <StatusBadge
                                                    value={invoice.state}
                                                    label={getInvoiceStateLabel(invoice.state)}
                                                    states={{
                                                        green: ['PAID', 'REFUNDED'],
                                                        yellow: ['OPEN', 'VOID', 'REVERSED'],
                                                        red: ['UNCOLLECTIBLE'],
                                                        gray: ['DRAFT', 'INVOICE_NUMBER_PENDING']
                                                    }}
                                                />
                                            </td>
                                            <td className="py-1.5 pl-3 whitespace-nowrap">
                                                {invoice.settledAt
                                                    ? <FormattedDate value={invoice.settledAt} day="2-digit" month="2-digit" year="numeric"/>
                                                    : <span className="text-gray-400">—</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
        </div>
    );
};

export default AnalysisOpenItemsView;
